import { PATHS } from 'routes/constants';
import { notifyBackendOfLogout, requestPasswordLogin, tokenClient } from './authApi';
import { MIN_REFRESH_DELAY_MS, REDIRECT_URI, TOKEN_EXPIRY_SKEW_MS, USE_KEYCLOAK } from './config';
import { readRealmRoles, readSubject } from './jwt';
import { oidcSession } from './oidcSession';
import { createCodeChallenge, createCodeVerifier } from './pkce';
import { sessionMonitor } from './sessionMonitor';
import { requestSilentAuthorization } from './silentSso';
import { tabSync } from './tabSync';
import { tokenStore } from './tokenStore';
import { EAuthStatus, TAuthListener, TAuthTokens } from './types';
import { buildAuthorizeUrl } from './urls';

const DEFAULT_RETURN_TO = PATHS.HOME_PAGE;

const MAX_HANDSHAKE_RETRIES = 3;

const describeExchangeError = (error: unknown): string => {
	const response = (error as { response?: { data?: { error?: string }; status?: number } })
		?.response;
	if (response?.data?.error) return response.data.error;
	if (response?.status) return `token_request_${response.status}`;
	return 'token_request_failed';
};

let status: EAuthStatus = EAuthStatus.Unknown;
const listeners = new Set<TAuthListener>();

let refreshPromise: Promise<string | null> | null = null;

let sessionPromise: Promise<unknown> | null = null;

let sessionChangePromise: Promise<void> | null = null;

let refreshTimerId = 0;

let redirecting = false;

const setStatus = (next: EAuthStatus): void => {
	if (status === next) return;
	status = next;
	listeners.forEach((listener) => listener(next));
};

// Realm roles of the current access token (Keycloak `realm_access.roles`). The manager does not
// import the store (that would close an import cycle through the auth reducer), so the app
// subscribes here and mirrors the roles into redux (`auth.realmRoles`).
type TRealmRolesListener = (roles: string[]) => void;
const realmRolesListeners = new Set<TRealmRolesListener>();
let realmRoles: string[] = [];

const publishRealmRoles = (next: string[]): void => {
	if (next.length === realmRoles.length && next.every((role, i) => role === realmRoles[i])) return;
	realmRoles = next;
	realmRolesListeners.forEach((listener) => listener(next));
};

const cancelScheduledRefresh = (): void => {
	window.clearTimeout(refreshTimerId);
	refreshTimerId = 0;
};

const scheduleProactiveRefresh = (expiresAt: number): void => {
	cancelScheduledRefresh();
	const delay = Math.max(expiresAt - Date.now() - TOKEN_EXPIRY_SKEW_MS, MIN_REFRESH_DELAY_MS);
	refreshTimerId = window.setTimeout(() => {
		void runRefresh();
	}, delay);
};

const adoptTokens = (tokens: TAuthTokens): void => {
	tokenStore.set(tokens);
	// Before the status flips, so pages that render on `Authenticated` already see the roles.
	publishRealmRoles(readRealmRoles(tokens.accessToken));
	oidcSession.clearSignedOut();
	oidcSession.clearHandshakeFailures();
	scheduleProactiveRefresh(tokens.expiresAt);
	if (USE_KEYCLOAK) sessionMonitor.start(tokens.sessionState);
	setStatus(EAuthStatus.Authenticated);
};

const applyTokens = (tokens: TAuthTokens): void => {
	adoptTokens(tokens);
	tabSync.publishTokens(tokens);
};

const endSession = (): void => {
	cancelScheduledRefresh();
	sessionMonitor.stop();
	tokenStore.clear();
	publishRealmRoles([]);
	setStatus(EAuthStatus.Unauthenticated);
};

tabSync.listen({
	onTokens: adoptTokens,
	onSignedOut: () => {
		oidcSession.markSignedOut();
		endSession();
	},
});

const establishSessionSilently = async (): Promise<TAuthTokens> => {
	const authorization = await requestSilentAuthorization();
	const tokens = await tokenClient.exchangeAuthorizationCode({
		code: authorization.code,
		sessionState: authorization.sessionState,
		redirectUri: authorization.redirectUri,
		...(authorization.codeVerifier ? { codeVerifier: authorization.codeVerifier } : {}),
	});
	if (!tokens) throw new Error('Token endpoint returned no access token');
	return tokens;
};

const performRefresh = async (): Promise<string | null> => {
	const refreshToken = tokenStore.getRefreshToken();

	if (refreshToken) {
		try {
			const tokens = await tokenClient.requestRefreshedTokens(refreshToken);
			if (tokens) {
				applyTokens(tokens);
				return tokens.accessToken;
			}
		} catch {
			/* empty */
		}
	}

	if (USE_KEYCLOAK && !oidcSession.isSignedOut()) {
		try {
			const tokens = await establishSessionSilently();
			applyTokens(tokens);
			return tokens.accessToken;
		} catch {
			/* empty */
		}
	}

	endSession();
	return null;
};

function runRefresh(): Promise<string | null> {
	if (refreshPromise) return refreshPromise;
	refreshPromise = performRefresh().finally(() => {
		refreshPromise = null;
	});
	return refreshPromise;
}

// `changed` is also what the iframe answers when it simply cannot read Keycloak's session cookie,
// so it is a hint to go and check, never proof that the session is over. The refresh token is the
// authority here: it needs no iframe and no third-party cookie. Only when it is rejected, and the
// silent check behind it too, is the session really gone.
let falsePositiveChanges = 0;
const FALSE_POSITIVE_LIMIT = 3;

const handleSessionChanged = (): void => {
	if (refreshPromise || sessionChangePromise || status !== EAuthStatus.Authenticated) return;

	const previousSubject = readSubject(tokenStore.getAccessToken());
	const previousSessionState = tokenStore.get()?.sessionState ?? null;

	sessionChangePromise = runRefresh()
		.then((accessToken) => {
			// Session confirmed gone; runRefresh has already torn it down.
			if (!accessToken) return;

			if (previousSubject && readSubject(accessToken) !== previousSubject) {
				window.location.reload();
				return;
			}

			if (tokenStore.get()?.sessionState !== previousSessionState) {
				falsePositiveChanges = 0;
				return;
			}

			// Nothing actually changed. An environment where that keeps happening — a browser or a
			// deployment where the iframe never sees the cookie — would otherwise refresh forever.
			falsePositiveChanges += 1;
			if (falsePositiveChanges >= FALSE_POSITIVE_LIMIT) sessionMonitor.disable();
		})
		.finally(() => {
			sessionChangePromise = null;
		});
};

sessionMonitor.configure({
	onChanged: handleSessionChanged,
	onUnsupported: () => {
		/* empty */
	},
});

document.addEventListener('visibilitychange', () => {
	if (document.visibilityState !== 'visible') return;
	if (status !== EAuthStatus.Authenticated) return;
	if (!tokenStore.isFresh()) void runRefresh();
	sessionMonitor.checkNow();
});

const runBootstrap = async (): Promise<void> => {
	if (oidcSession.isSignedOut()) {
		setStatus(EAuthStatus.Unauthenticated);
		return;
	}

	if (window.location.pathname === PATHS.AUTH_CODE_PAGE) {
		setStatus(EAuthStatus.Authenticating);
		return;
	}

	if (!USE_KEYCLOAK) {
		if (!tokenStore.getRefreshToken()) {
			setStatus(EAuthStatus.Unauthenticated);
			return;
		}
		setStatus(EAuthStatus.Authenticating);
		await runRefresh();
		return;
	}

	setStatus(EAuthStatus.Authenticating);
	try {
		applyTokens(await establishSessionSilently());
	} catch {
		setStatus(EAuthStatus.Unauthenticated);
	}
};

export const authManager = {
	getStatus: (): EAuthStatus => status,

	subscribe: (listener: TAuthListener): (() => void) => {
		listeners.add(listener);
		return () => listeners.delete(listener);
	},

	/** Realm roles of the current access token; a late subscriber gets the current ones right away. */
	subscribeRealmRoles: (listener: (roles: string[]) => void): (() => void) => {
		realmRolesListeners.add(listener);
		if (realmRoles.length > 0) listener(realmRoles);
		return () => realmRolesListeners.delete(listener);
	},

	isSignedOut: (): boolean => oidcSession.isSignedOut(),

	/** Why the last handshake gave up, for the sign-in screen to show. */
	getHandshakeFailureReason: (): string | null => oidcSession.handshakeFailureReason(),

	/** Where the user was heading when the handshake started, so a retry does not drop them home. */
	getPendingReturnTo: (): string | null => oidcSession.peekReturnTo(),

	/** Asking to sign in by hand restores the retry budget the automatic attempts used up. */
	signInAgain: (returnTo?: string): Promise<void> => {
		oidcSession.clearHandshakeFailures();
		oidcSession.clearSignedOut();
		return authManager.loginRedirect(returnTo);
	},

	bootstrap: (): Promise<unknown> => {
		if (!sessionPromise) sessionPromise = runBootstrap();
		return sessionPromise;
	},

	getAccessToken: async (): Promise<string | null> => {
		if (sessionPromise) await sessionPromise.catch(() => undefined);
		if (tokenStore.isFresh()) return tokenStore.getAccessToken();
		if (!tokenStore.get() && !tokenStore.getRefreshToken()) return null;
		return runRefresh();
	},

	recoverFromUnauthorized: async (usedAccessToken: string | null): Promise<string | null> => {
		const current = tokenStore.getAccessToken();
		if (current && current !== usedAccessToken) return current;
		if (status === EAuthStatus.Unauthenticated && !tokenStore.getRefreshToken()) return null;
		return runRefresh();
	},

	loginRedirect: async (returnTo?: string): Promise<void> => {
		if (redirecting) return;
		redirecting = true;

		const target = returnTo ?? `${window.location.pathname}${window.location.search}`;
		const codeVerifier = createCodeVerifier();
		const codeChallenge = await createCodeChallenge(codeVerifier);
		const state = oidcSession.beginInteractive(target, codeVerifier);

		oidcSession.clearSignedOut();
		window.location.assign(buildAuthorizeUrl({ state, redirectUri: REDIRECT_URI, codeChallenge }));
	},

	handleAuthorizationCallback: (search: string): Promise<string> => {
		// A failed handshake used to raise the signed-out wall on the very first attempt, which turned
		// any transient failure into a screen the user had to click through by hand. Only a run of
		// them means something is actually broken; below the limit the sign-in screen retries by
		// itself, exactly as it does for a session that was never established.
		const failure = (reason: string): Error => {
			const attempts = oidcSession.recordHandshakeFailure(reason);
			if (attempts > MAX_HANDSHAKE_RETRIES) oidcSession.markSignedOut();
			endSession();
			return new Error(reason);
		};

		const run = async (): Promise<string> => {
			const params = new URLSearchParams(search);
			const returnTo = oidcSession.peekReturnTo();
			const handshake = oidcSession.consumeInteractive(params.get('state'));
			const error = params.get('error');
			const code = params.get('code');

			if (error) throw failure(error);
			if (!code) throw failure('missing_code');
			if (!handshake.valid) throw failure('state_mismatch');

			setStatus(EAuthStatus.Authenticating);

			let tokens: TAuthTokens | null;
			try {
				tokens = await tokenClient.exchangeAuthorizationCode({
					code,
					sessionState: params.get('session_state') ?? '',
					redirectUri: REDIRECT_URI,
					...(handshake.codeVerifier ? { codeVerifier: handshake.codeVerifier } : {}),
				});
			} catch (exchangeError) {
				throw failure(describeExchangeError(exchangeError));
			}

			if (!tokens) throw failure('empty_token_response');
			applyTokens(tokens);
			oidcSession.clearReturnTo();

			return returnTo && returnTo !== PATHS.AUTH_CODE_PAGE ? returnTo : DEFAULT_RETURN_TO;
		};

		const promise = run();
		sessionPromise = promise;
		return promise;
	},

	passwordLogin: async (login: string, password: string): Promise<void> => {
		const tokens = await requestPasswordLogin(login, password);
		if (!tokens) throw new Error('Login endpoint returned no access token');
		applyTokens(tokens);
	},

	logout: async (): Promise<void> => {
		const refreshToken = tokenStore.getRefreshToken();
		const accessToken = tokenStore.getAccessToken();

		oidcSession.markSignedOut();
		cancelScheduledRefresh();
		sessionMonitor.stop();

		if (refreshToken) {
			// Ends the SSO session at Keycloak, then lets the API know so its own bookkeeping keeps up.
			// On Neman both of these are the same call, so only the second one runs.
			if (USE_KEYCLOAK) {
				try {
					await tokenClient.endSsoSession(refreshToken, accessToken);
				} catch {
					/* empty */
				}
			}

			try {
				await notifyBackendOfLogout(refreshToken, accessToken);
			} catch {
				/* empty */
			}
		}

		tokenStore.clear();
		publishRealmRoles([]);
		setStatus(EAuthStatus.Unauthenticated);
		tabSync.publishSignedOut();
	},
};
